import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Brain, Building2, Users, Target, 
  Lightbulb, ShieldCheck, Briefcase, TrendingUp, Compass, 
  Zap, AlertTriangle, Trophy, Eye, Rocket
} from "lucide-react";
import { KnowledgeScoreIndicator } from "./KnowledgeScoreIndicator";
import type { CompanyKnowledgeProfile, BusinessProfile } from "./types";
import { formatDistanceToNow } from "date-fns";

interface CompanyKnowledgeDetailsDialogProps {
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestToken?: string;
}

export function CompanyKnowledgeDetailsDialog({ 
  profileId, 
  open, 
  onOpenChange,
  guestToken 
}: CompanyKnowledgeDetailsDialogProps) {
  const { data: profile, isLoading } = useQuery({
    queryKey: ["company-knowledge-profile-detail", profileId, guestToken],
    queryFn: async () => {
      if (guestToken) {
        const { data, error } = await supabase.rpc('guest_get_company_profile', {
          token_uuid: guestToken,
          profile_uuid: profileId
        });
        if (error) throw error;
        if (!data || data.length === 0) throw new Error('Profile not found');
        return data[0] as CompanyKnowledgeProfile;
      }

      const { data, error } = await supabase
        .from("company_knowledge_profiles")
        .select("*")
        .eq("id", profileId)
        .single();

      if (error) throw error;
      return data as CompanyKnowledgeProfile;
    },
    enabled: open && !!profileId,
  });

  const businessProfile = (profile?.business_profile || {}) as BusinessProfile;

  const renderField = (label: string, value: string | string[] | undefined, icon: React.ReactNode) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
          <div className="text-muted-foreground">{icon}</div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-sm text-muted-foreground/50 italic">Not yet collected</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
        <div className="text-primary">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          {Array.isArray(value) ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {value.map((v, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground whitespace-pre-wrap">{value}</p>
          )}
        </div>
      </div>
    );
  };

  const formatBusinessModel = (model: string | undefined) => {
    if (!model) return undefined;
    const modelMap: Record<string, string> = {
      'b2b': 'B2B (Business to Business)',
      'b2c': 'B2C (Business to Consumer)',
      'd2c': 'D2C (Direct to Consumer)',
      'marketplace': 'Marketplace',
    };
    return modelMap[model] || model;
  };

  const formatCompanyStage = (stage: string | undefined) => {
    if (!stage) return undefined;
    const stageMap: Record<string, string> = {
      'pre_launch': 'Pre-launch / Idea Stage',
      'early': 'Early Stage (0-2 years)',
      'growth': 'Growth Stage',
      'established': 'Established / Mature',
    };
    return stageMap[stage] || stage;
  };

  const formatCompetitiveLandscape = (landscape: string | undefined) => {
    if (!landscape) return undefined;
    const landscapeMap: Record<string, string> = {
      'blue_ocean': 'Blue Ocean (Few competitors)',
      'moderate': 'Moderate Competition',
      'crowded': 'Crowded / Highly Competitive',
    };
    return landscapeMap[landscape] || landscape;
  };

  const formatTechMaturity = (maturity: string | undefined) => {
    if (!maturity) return undefined;
    const maturityMap: Record<string, string> = {
      'manual': 'Manual / Spreadsheets',
      'basic_crm': 'Basic CRM',
      'advanced': 'Advanced / Multiple Tools',
    };
    return maturityMap[maturity] || maturity;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-primary/20 border border-primary/20">
                <Brain className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg">
                  {isLoading ? <Skeleton className="h-6 w-32" /> : profile?.company_name}
                </DialogTitle>
                {profile && (
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(new Date(profile.updated_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>
            {profile && (
              <KnowledgeScoreIndicator score={profile.knowledge_score} variant="ring" size="md" />
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        ) : profile ? (
          <div className="flex-1 overflow-auto p-6">
            <div className="space-y-6">
              {/* Phase 1: The Basics */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">1</span>
                  The Basics
                </h3>
                <div className="grid gap-3">
                  {renderField("Business Model", formatBusinessModel(businessProfile.business_model), <Briefcase className="h-4 w-4" />)}
                  {renderField("Industry", businessProfile.industry, <Building2 className="h-4 w-4" />)}
                  {renderField("Core Problem Solved", businessProfile.core_problem_solved, <Lightbulb className="h-4 w-4" />)}
                  {renderField("Target Audience", businessProfile.target_audience, <Users className="h-4 w-4" />)}
                </div>
              </div>

              {/* Phase 2: Growth & Operations */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">2</span>
                  Growth & Operations
                </h3>
                <div className="grid gap-3">
                  {renderField("Company Stage", formatCompanyStage(businessProfile.company_stage), <TrendingUp className="h-4 w-4" />)}
                  {renderField("Team Size", businessProfile.company_size, <Users className="h-4 w-4" />)}
                  {renderField("Acquisition Channels", businessProfile.acquisition_channels, <Compass className="h-4 w-4" />)}
                  {renderField("Tech Maturity", formatTechMaturity(businessProfile.tech_maturity), <Zap className="h-4 w-4" />)}
                </div>
              </div>

              {/* Phase 3: Pain & Goals */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">3</span>
                  Pain & Goals
                </h3>
                <div className="grid gap-3">
                  {renderField("Magic Wand Fix", businessProfile.magic_wand_fix, <Zap className="h-4 w-4" />)}
                  {renderField("Biggest Bottleneck", businessProfile.biggest_bottleneck, <AlertTriangle className="h-4 w-4" />)}
                  {renderField("6-Month Priority", businessProfile.six_month_priority, <Target className="h-4 w-4" />)}
                  {renderField("Competitive Landscape", formatCompetitiveLandscape(businessProfile.competitive_landscape), <Eye className="h-4 w-4" />)}
                  {renderField("Key Competitor", businessProfile.key_competitor, <ShieldCheck className="h-4 w-4" />)}
                  {renderField("Competitor Strength", businessProfile.competitor_strength, <Trophy className="h-4 w-4" />)}
                </div>
              </div>

              {/* Phase 4: Success & Future */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">4</span>
                  Success & Future
                </h3>
                <div className="grid gap-3">
                  {renderField("Competitive Advantage", businessProfile.competitive_advantage, <Trophy className="h-4 w-4" />)}
                  {renderField("Success Metric", businessProfile.success_metric, <TrendingUp className="h-4 w-4" />)}
                  {renderField("3-Year Vision", businessProfile.three_year_vision, <Rocket className="h-4 w-4" />)}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
